import {
  createContext,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { useModal } from '../Modal/useModal';
import { DataUnit, DataUnitStatsItem, SourceTotal } from '@/core/types';
import { normalizeDateFE } from '@/core/normalization';
import { activeCompanySelector } from '@/core/store/user/userSlice';
import { useAppSelector } from '@/hooks';
import { getDataUntisSourceTotals, getPurchasePricesCount } from '@/core/api';
interface ConfirmDataUnitModalContextType {
  isModalOpened: boolean;
  closeModal: () => void;
  openModal: () => void;
  activeDU?: DataUnit;
  activeDUStats?: DataUnitStatsItem;
  setActiveDU: Dispatch<SetStateAction<DataUnit | undefined>>;
  setActiveDuStats: Dispatch<SetStateAction<DataUnitStatsItem | undefined>>;
  activeDUDate?: Date;
  isLoading: boolean;
  sourceTotals: SourceTotal[];
  activeDuPPCount: number | undefined;
}

export const ConfirmDataUnitModalContext =
  createContext<ConfirmDataUnitModalContextType>(
    {} as ConfirmDataUnitModalContextType
  );

export function ConfirmDataUnitModalProvider({
  children,
}: {
  children: ReactNode;
}) {
  const activeCompany = useAppSelector(activeCompanySelector);
  const controller = useRef(new AbortController());
  const [activeDU, setActiveDU] = useState<DataUnit>();
  const [activeDUStats, setActiveDuStats] = useState<DataUnitStatsItem>();
  const [isLoading, setIsLoading] = useState(false);
  const [sourceTotals, setSourceTotals] = useState<SourceTotal[]>([]);
  const [activeDuPPCount, setActiveDuPPCount] = useState<number>();

  const { isModalOpened, closeModal, openModal } = useModal();

  const activeDUDate = useMemo(() => {
    return activeDU ? normalizeDateFE(activeDU.date) : undefined;
  }, [activeDU]);
  const id = activeDU?.id;
  const companyId = activeCompany?.company_id;

  useEffect(() => {
    if (activeDUDate && companyId && isModalOpened) {
      const year = String(new Date(activeDUDate).getFullYear());
      const month = String(new Date(activeDUDate).getMonth() + 1);
      setIsLoading(true);
      getPurchasePricesCount(
        companyId,
        controller.current,
        undefined,
        year,
        month
      )
        .then((res) => {
          setActiveDuPPCount(res);
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  }, [isModalOpened, companyId, activeDUDate]);

  useEffect(() => {
    if (isModalOpened && id && companyId) {
      setIsLoading(true);
      getDataUntisSourceTotals(companyId, id)
        .then((res) => {
          setIsLoading(false);
          setSourceTotals(res || []);
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  }, [isModalOpened, id, companyId]);

  return (
    <ConfirmDataUnitModalContext.Provider
      value={{
        isModalOpened,
        closeModal,
        openModal,
        activeDU,
        activeDUStats,
        setActiveDU,
        setActiveDuStats,
        activeDUDate,
        isLoading,
        sourceTotals,
        activeDuPPCount,
      }}
    >
      {children}
    </ConfirmDataUnitModalContext.Provider>
  );
}
